/**
 * Team card job count badges
 *
 * Fetches job postings from the configured source and injects live
 * "N open positions" badges into the team cards on the careers
 * landing page.
 *
 * @package Interact_26
 */

import { fetchPostings } from './api';
import { type PinpointJob, type CareersConfig } from './types';
import { normalizePosting, matchesDepartmentFilter } from './utils';

const BADGE_CLASS = 'careers-teams__card-badge';
const LOADED_CLASS = 'careers-teams__card-badge--loaded';
const HIDDEN_CLASS = 'careers-teams__card-badge--hidden';

/**
 * Count jobs that match a department filter value.
 *
 * Uses the same `matchesDepartmentFilter` logic as the jobs page
 * to ensure counts are consistent. For the "all" card, returns
 * the total number of jobs.
 */
const countJobsForFilter = (filter: string, jobs: PinpointJob[], config: CareersConfig): number => {
  if (filter === 'all') {
    return jobs.length;
  }

  const configWithFilter: CareersConfig = { ...config, departmentFilter: filter };
  return jobs.filter((job) => matchesDepartmentFilter(job, configWithFilter)).length;
};

/**
 * Format a job count into a human-readable badge label.
 */
const formatBadgeText = (count: number): string => {
  if (count === 0) return 'No open positions';
  if (count === 1) return '1 open position';
  return `${count} open positions`;
};

/**
 * Initialize job count badges on team cards.
 *
 * Bails early if no team cards or no config is found.
 * On fetch error, badges are hidden silently (graceful degradation).
 */
export const initTeamJobCounts = async (): Promise<void> => {
  const cards = document.querySelectorAll<HTMLElement>(
    '.careers-teams__card[data-department-filter]',
  );

  if (cards.length === 0) {
    return;
  }

  const config = window.InteractCareersData ?? null;

  if (!config) {
    return;
  }

  try {
    const postings = await fetchPostings(config);
    const jobs = postings.map(normalizePosting);

    cards.forEach((card) => {
      const filter = card.dataset.departmentFilter;
      const badge = card.querySelector<HTMLElement>(`.${BADGE_CLASS}`);

      if (!filter || !badge) return;

      const count = countJobsForFilter(filter, jobs, config);
      badge.textContent = formatBadgeText(count);
      badge.classList.add(LOADED_CLASS);
    });
  } catch {
    // Graceful degradation — hide all badges on error
    cards.forEach((card) => {
      const badge = card.querySelector<HTMLElement>(`.${BADGE_CLASS}`);
      if (badge) {
        badge.classList.add(HIDDEN_CLASS);
      }
    });
  }
};
